
import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Template and config actions
import { fetchAllTemplates } from './redux/actions/templateActions';
import { fetchGlobalConfig } from './redux/actions/globalConfigActions';

// RTK slices
import { setToken, resetInitialization, setInitializing, setInitialized } from './redux/slices/appSlice';
import { fetchAllModels } from './redux/slices/modelsSlice';
import { fetchAllAssessments, initializeAssessmentLocal } from './redux/slices/assessmentsSlice';
import { fetchAssessmentRisks } from './redux/slices/risksSlice';
// Security actions for loading scenarios (needed for RiskLandscape security charts)
import { fetchScenarios } from './redux/slices/securitySlice';
// Control actions for loading saved controls (needed for RiskLandscape policy coverage chart)
import { setSavedControls } from './redux/actions/controlActions';

import { Route, Routes, Navigate } from 'react-router-dom';
import { AppShell, Transition } from '@mantine/core';
import RiskCardForm from './RiskCardForm.js';
import RiskCatalogue from './legacy/RiskCatalogue.js';
import AutopilotAssessmentFlow from './autopilotMode/AutopilotAssessmentFlow';
import ExplorerAutopilotFlow from './explorerMode/ExplorerAutopilotFlow';
import NavigatorAssessmentFlow from './navigatorMode/NavigatorAssessmentFlow';
// Removed RiskEvaluationLegacy import - component preserved but not routed
import ResidualRisk from './ResidualRisk.js';
import ModelInventory from './ModelInventory.js';
// REMOVED - ModelDetailsModal deleted as part of legacy cleanup
import AssessmentCompleted from './AssessmentCompleted.js';
import RiskFormManager from './riskFormManager.js';
import RiskThemes from './legacy/RiskThemes.js';
import ContextAssessment from './contextAssessment.js';
import RiskLandscape from './RiskLandscape';
// RisksBySource removed - now rendered in RiskLandscape table view
import RiskReports from './RiskReports';
import RiskAssessments from './RiskAssessmentsMantine';
import { useAuth0 } from '@auth0/auth0-react';
import EnhancedLoadingScreen from './components/EnhancedLoadingScreen.js';
import FileUpload from './fileUploader.js';
import UniversalFileUploader from './UniversalFileUploader.jsx';
import {jwtDecode} from 'jwt-decode';
// REMOVED - AdminConfigHome deleted as part of legacy config cleanup
import AssessmentReport from './AssessmentReport';
import StageReport   from './StageReport';
import ExplorerAssessmentCanvas from './canvas/ExplorerAssessmentCanvas';
import NavigatorAssessmentCanvas from './canvas/NavigatorAssessmentCanvas';
import { isRiskManager, hasAuthorizedRole } from './utils/roleHelpers';
import { Header } from './components/Header';
import TemplateManagerHome from './templateManager/TemplateManagerHome';
import TemplateEditor from './templateManager/TemplateEditor';
import ModelFormConfigEditor from './globalConfigs/ModelFormConfigEditor';
import PromptEditorPage from './pages/PromptEditor';
import AgentManagerPage from './pages/AgentManager';
import useInactivityTimeout from './hooks/useInactivityTimeout';
import './App.css';

// Agency (lazy-loaded)
const AgencyLayout = React.lazy(() => import('./agency/components/AgencyLayout'));
const AgencyHome = React.lazy(() => import('./agency/pages/AgencyHome'));
const AgencyDashboard = React.lazy(() => import('./agency/pages/AgencyDashboard'));
const CreateAssessmentPage = React.lazy(() => import('./agency/pages/CreateAssessment'));
const AssessmentDetail = React.lazy(() => import('./agency/pages/AssessmentDetail'));
const AgencyFindingsPage = React.lazy(() => import('./agency/pages/AgencyFindingsPage'));
const AgencyReportPage = React.lazy(() => import('./agency/pages/AgencyReportPage'));
const AgencyReportsList = React.lazy(() => import('./agency/pages/AgencyReportsList'));
const AgencyLandscapePage = React.lazy(() => import('./agency/pages/AgencyLandscapePage'));
const AgencyAgentsPage = React.lazy(() => import('./agency/pages/AgencyAgentsPage'));
const TeamDashboard = React.lazy(() => import('./agency/pages/TeamDashboard'));
const ServiceDesk = React.lazy(() => import('./agency/pages/ServiceDesk'));
const SkillsHome = React.lazy(() => import('./skillManager/SkillsHome'));
const SkillEditor = React.lazy(() => import('./skillManager/SkillEditor'));
const MethodsHome = React.lazy(() => import('./methodManager/MethodsHome'));
const MethodEditor = React.lazy(() => import('./methodManager/MethodEditor'));
const SimplifiedAssessmentLaunch = React.lazy(() => import('./agency/pages/SimplifiedAssessmentLaunch'));
const ToolsSkillsPage = React.lazy(() => import('./agency/pages/ToolsSkillsPage'));
const FrameworksPage = React.lazy(() => import('./agency/pages/FrameworksPage'));
const OrganisationPage = React.lazy(() => import('./agency/pages/OrganisationPage'));
const IntegrationsPage = React.lazy(() => import('./agency/pages/IntegrationsPage'));
const RiskTaxonomyPage = React.lazy(() => import('./agency/pages/RiskTaxonomyPage'));
const ThreatAgentsPage = React.lazy(() => import('./agency/pages/ThreatAgentsPage'));
const ContextLibraryPage = React.lazy(() => import('./agency/pages/ContextLibraryPage'));

function App() {
  const { user, isAuthenticated, isLoading, logout, getAccessTokenSilently} = useAuth0();

  // New RTK selectors (primary source)
  const modelsLoading = useSelector(state => state.models.loading);
  const assessmentsLoading = useSelector(state => state.assessments.loading?.all);
  const appInitializing = useSelector(state => state.app.isInitializing);

  const [userRoles, setUserRoles] = useState([]);
  const [fadeIn, setFadeIn] = useState(false);
  // Only show full animated loading screen during INITIAL app load (appInitializing)
  // Subsequent data refreshes (modelsLoading/assessmentsLoading) should not trigger the animated screen
  const showInitialLoadingScreen = isLoading || appInitializing;
  console.log('[App] Loading states:', { isLoading, modelsLoading, assessmentsLoading, appInitializing, showInitialLoadingScreen });
  const userIsRiskManager = useMemo(() => isRiskManager(userRoles), [userRoles]);
  const userIsAuthorized = useMemo(() => hasAuthorizedRole(userRoles), [userRoles]);
  const dispatch = useDispatch();
  useInactivityTimeout();

  // Use new RTK models state as the single source of truth
  const modelsById = useSelector((state) => state.models.byId);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Reset initialization state to ensure loading screen shows
        // even if redux-persist hydrated old state with isInitialized=true
        dispatch(resetInitialization());
        dispatch(setInitializing(true));

        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai',
            scope: 'read:models write:models delete:models'
          }
        });

        console.log('Token obtained, initializing app...');

        // Store token in Redux for RTK thunks to use
        dispatch(setToken(token));

        // Parallel data fetches using RTK thunks - single source of truth
        // Templates and config are non-blocking - Supabase free tier hibernates causing fetch failures
        // Templates ARE required for creating assessments, but not for viewing existing data
        const [, assessmentsResult] = await Promise.all([
          dispatch(fetchAllModels()),       // RTK thunk (uses token from state)
          dispatch(fetchAllAssessments()),  // RTK thunk
          dispatch(fetchAllTemplates({ publishedOnly: true })).catch(err => {
            console.warn('[App] Templates unavailable (Supabase may be hibernating). You can view existing data, but cannot create new assessments until templates load.');
            return { payload: [] };
          }),
          dispatch(fetchGlobalConfig('legacy_risk_themes')).catch(err => {
            console.warn('[App] Legacy risk themes unavailable (non-critical, templates have impact_categories)');
            return { payload: null };
          }),
        ]);

        // Initialize state.risks.byAssessment for each assessment so RiskLandscape works
        // This loads saved risks into modelRisks.areaRisks
        const assessments = assessmentsResult?.payload || [];
        console.log('[App] Initializing risk state for', assessments.length, 'assessments');
        await Promise.all(assessments.map(async (assessment) => {
          const modelId = assessment.model_uuid;
          const assessmentUuid = assessment.assessment_uuid;
          if (assessmentUuid) {
            try {
              // Initialize assessment in RTK assessmentsSlice (now MERGES, doesn't overwrite)
              dispatch(
                initializeAssessmentLocal({
                  assessmentUuid,
                  modelId,
                  modelName: assessment.model_name,
                  assessmentName: assessment.assessment_name || assessment.model_name,
                  assessmentStatus: assessment.assessment_status,
                })
              );
              // Fetch saved risks into risksSlice via RTK async thunk
              await dispatch(fetchAssessmentRisks({ assessmentUuid, modelId }));
              // Fetch security scenarios for security charts in RiskLandscape
              await dispatch(fetchScenarios({ modelId, assessmentUuid }));
              // Fetch saved controls for policy coverage chart in RiskLandscape
              await dispatch(setSavedControls(assessmentUuid, modelId));
            } catch (err) {
              console.warn(`[App] Failed to load data for assessment ${assessmentUuid}:`, err);
            }
          }
        }));
        console.log('[App] Risk, security, and controls state initialized');

        dispatch(setInitialized(true));
      } catch (error) {
        console.error('Error initializing app:', error);
        // Still mark as initialized so the loading screen dismisses —
        // partial data is better than an infinite loading screen
        dispatch(setInitialized(true));
        if (error.error === 'login_required' || error.error === 'consent_required') {
          logout({ logoutParams: { returnTo: window.location.origin } });
        }
      }
    };

    if (isAuthenticated) {
      initializeApp();
    }
  }, [dispatch, getAccessTokenSilently, isAuthenticated, logout]);

  const hasNotStarted = Object.values(modelsById).some(
    (model) => (model.assessment_status || '').toLowerCase() === 'not started'
  );

  const hasPendingReviews = Object.values(modelsById).some(
    (model) => model.review_status === 'submitted'
  );

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai', 
            scope: 'openid profile email read:models write:models delete:models' 
          }
        });

        const decoded = jwtDecode(token);
        const roles = decoded['https://agenthuginn.ai/claims/roles'] || decoded['https://riskgpt.io/claims/roles'] || [];
        setUserRoles(roles);
        console.log('User roles:', roles);
      } catch (error) {
        console.error('Error fetching roles:', error);
      }
    };

    if (isAuthenticated) {
      fetchRoles();
    }
  }, [isAuthenticated, getAccessTokenSilently]);





  useEffect(() => {
    if (!showInitialLoadingScreen) {
      setFadeIn(true);
    }
  }, [showInitialLoadingScreen]);

  // Only show the animated loading screen during initial app load
  // Subsequent data refreshes (e.g., after creating assessment) should not show this
  if (showInitialLoadingScreen) {
    return (
      <EnhancedLoadingScreen
        onComplete={() => {
          // Ensure we stop showing the loading screen immediately after chat completes
          // by flipping modelsLoading false if the fetch already finished.
          // If still loading, fallback to state change from reducer.
        }}
      />
    );
  }

  return (
    <div className="App">
    <AppShell
      header={{ height: 68 }}
      padding="md"
      styles={{
        main: { backgroundColor: '#F5F5F5' }
      }}
    >
      <AppShell.Header>
        <Header
          user={user}
          isAuthenticated={isAuthenticated}
          userRoles={userRoles}
          hasNotStarted={hasNotStarted}
          hasPendingReviews={hasPendingReviews}
          userIsRiskManager={userIsRiskManager}
          onLogout={() => logout({ logoutParams: { returnTo: window.location.origin } })}
        />
      </AppShell.Header>

      <AppShell.Main>
        <Transition mounted={fadeIn} transition="fade" duration={800} timingFunction="ease">
          {(styles) => (
            <div style={styles}>
              {/* Gate: only users with an explicit role can access the app */}
              {isAuthenticated && userRoles.length > 0 && !userIsAuthorized ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', textAlign: 'center' }}>
                  <div>
                    <h2 style={{ margin: '0 0 8px', fontWeight: 600 }}>Access Denied</h2>
                    <p style={{ color: '#868e96', margin: '0 0 16px' }}>
                      Your account does not have an authorized role. Contact your administrator to request access.
                    </p>
                    <button
                      onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                      style={{ padding: '8px 20px', cursor: 'pointer', borderRadius: 6, border: '1px solid #dee2e6', background: '#fff' }}
                    >
                      Sign out
                    </button>
                  </div>
                </div>
              ) : (
              <Routes>
          {/* Top-level routes (no sidebar) */}
          <Route path="/" element={
            userIsRiskManager ? (
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading...</div>}>
                <ServiceDesk user={user} userRoles={userRoles} />
              </React.Suspense>
            ) : (
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading...</div>}>
                <TeamDashboard user={user} />
              </React.Suspense>
            )
          } />
          <Route path="/landscape" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading Landscape...</div>}>
              <AgencyLandscapePage />
            </React.Suspense>
          } />
          {/* Legacy AI landscape preserved for in-flight bookmarks; cleanup task will retire it */}
          <Route path="/legacy-landscape" element={<RiskLandscape />} />
          <Route path="/model-inventory" element={<ModelInventory />} />

          {/* Assessments — top-level */}
          <Route path="/assessments" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading...</div>}>
              <AgencyDashboard />
            </React.Suspense>
          } />
          <Route path="/assessments/new" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading...</div>}>
              <CreateAssessmentPage />
            </React.Suspense>
          } />
          <Route path="/assessments/:assessmentId" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading...</div>}>
              <AssessmentDetail />
            </React.Suspense>
          } />
          <Route path="/assessments/:assessmentId/findings" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading...</div>}>
              <AgencyFindingsPage />
            </React.Suspense>
          } />
          <Route path="/assessments/:assessmentId/report" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading...</div>}>
              <AgencyReportPage />
            </React.Suspense>
          } />

          {/* Simplified assessment launch (Team Member flow) */}
          <Route path="/start/:type" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading...</div>}>
              <SimplifiedAssessmentLaunch />
            </React.Suspense>
          } />

          {/* Reports — agency-native canvas */}
          <Route path="/reports" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading Reports...</div>}>
              <AgencyReportsList />
            </React.Suspense>
          } />

          {/* Backward-compat redirects */}
          <Route path="/risk-assessments" element={<RiskAssessments />} />
          {/* Legacy AI reports preserved for in-flight bookmarks; cleanup task will retire these */}
          <Route path="/legacy-reports" element={<RiskReports />} />
          <Route path="/risk-reports" element={<RiskReports />} />
          <Route path="/risks-by-model" element={<RiskReports />} />

          {/* Huginn AI flow routes (unchanged) */}
          <Route path="/risk-themes" element={<RiskThemes />} />
          <Route path="/risk-catalogue" element={<RiskCatalogue />} />
          <Route path="/risk-form-manager" element={<RiskFormManager />} />
          <Route path="/context-evaluation" element={<ContextAssessment />} />
          <Route path="/risk-form/:modelId/:modelName/:assessmentUuid" element={<RiskCardForm/>} />
          <Route path="/autopilot-assessment/:modelId/:assessmentUuid" element={<AutopilotAssessmentFlow/>} />
          <Route path="/navigator-assessment/:modelId/:assessmentUuid" element={<NavigatorAssessmentFlow/>} />
          <Route path="/navigator-report/:modelId/:assessmentUuid" element={<NavigatorAssessmentCanvas/>} />
          <Route path="/explorer-assessment/:modelId/:assessmentUuid" element={<ExplorerAutopilotFlow/>} />
          <Route path="/explorer-report/:modelId/:assessmentUuid" element={<ExplorerAssessmentCanvas/>} />
          <Route path="/risk-catalogue/:modelId/:modelName" element={<RiskCatalogue />} />
          <Route path="/residual-risk/:modelId/:assessmentUuid" element={<ResidualRisk />} />
          <Route path="/assessment-completed/:modelId/:modelName/:assessmentUuid" element={<AssessmentCompleted />} />
          <Route path="/assessment-report/:modelId/" element={<AssessmentReport />} />
          <Route path="/stage-report" element={<StageReport />} />
          <Route path="/file-upload" element={<FileUpload />} />

          {/* Legacy standalone routes (redirect to agency sidebar) */}
          <Route path="/universal-uploader" element={<UniversalFileUploader />} />
          <Route path="/template-manager" element={<TemplateManagerHome />} />
          <Route path="/template-editor/:templateId" element={<TemplateEditor />} />
          <Route path="/model-form-config" element={<ModelFormConfigEditor />} />
          <Route path="/prompts" element={<PromptEditorPage />} />
          <Route path="/agents" element={<AgentManagerPage />} />

          {/* Agency routes (with sidebar, lazy-loaded) */}
          <Route path="/agency" element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading Agency...</div>}>
              <AgencyLayout />
            </React.Suspense>
          }>
            <Route index element={<AgencyHome />} />
            <Route path="teams/risk" element={<AgencyDashboard />} />
            <Route path="teams/security" element={<AgencyDashboard />} />
            <Route path="teams/audit" element={<AgencyDashboard />} />
            <Route path="agents" element={<AgencyAgentsPage />} />
            <Route path="prompts" element={<PromptEditorPage />} />
            <Route path="templates" element={<TemplateManagerHome />} />
            <Route path="model-form" element={<ModelFormConfigEditor />} />
            <Route path="policies" element={<UniversalFileUploader categoryFilter={['policy-governance', 'policy-security', 'policy-ai-control']} />} />
            <Route path="frameworks" element={<FrameworksPage />} />
            <Route path="organisation" element={
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
                <OrganisationPage />
              </React.Suspense>
            } />
            <Route path="integrations" element={
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
                <IntegrationsPage />
              </React.Suspense>
            } />
            <Route path="risk-taxonomy" element={
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
                <RiskTaxonomyPage />
              </React.Suspense>
            } />
            <Route path="threat-agents" element={
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
                <ThreatAgentsPage />
              </React.Suspense>
            } />
            <Route path="context-library" element={
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
                <ContextLibraryPage />
              </React.Suspense>
            } />
            {/* Backwards compatible redirect: /agency/knowledge-base → Organisation */}
            <Route path="knowledge-base" element={<Navigate to="/agency/organisation" replace />} />
            <Route path="tools" element={<ToolsSkillsPage />} />
            <Route path="skills" element={
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading Skills...</div>}>
                <SkillsHome />
              </React.Suspense>
            } />
            <Route path="skills/:skillKey" element={
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading Skill...</div>}>
                <SkillEditor />
              </React.Suspense>
            } />
            <Route path="methods" element={
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading Methods...</div>}>
                <MethodsHome />
              </React.Suspense>
            } />
            <Route path="methods/:methodKey" element={
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading Method...</div>}>
                <MethodEditor />
              </React.Suspense>
            } />
          </Route>

          <Route path="*" element={<div>Not Found</div>} />
          </Routes>
              )}
            </div>
          )}
        </Transition>
      </AppShell.Main>
    </AppShell>
    </div>

  );
}

export default App;
